import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Container,
  Row,
  Col,
  DropdownItem,
  Input,
  Button,
  Badge,
  Offcanvas,
  OffcanvasBody,
} from "reactstrap";
import {
  AnalyticsCard,
  InterviewCard,
  DynamicTableWrapper,
  OffCanvasHeaderComponent,
  NewInterviewCanvas,
  InterviewCandidateCanvas,
  ModalFormWrapper,
} from "../components";
import ActionDropDown from "@workspace/common/src/Components/DynamicTable/Components/ActionDropDown";
import {
  useTableHook,
  DynamicTableHelper,
  DateHelper,
  OffCanvasWrapper,
} from "@workspace/common";
import {
  INTERVIEW_INITIAL_OPTIONS,
  INTERVIEW_MANDATORY_OPTIONS,
  datePickerConstants,
  statusMapping,
} from "./interviewListingConstants";
import {
  fetchInterviewListingDetails,
  fetchInterviewCounts,
  cancelInterview,
  addInterviewLogs,
  fetchJobCandidateId,
} from "../store/actions";
import { fetchJobTimelineFormSubmissionReset } from "@workspace/job/src/store/jobStage/action";
import TemplatePreviewSideDrawer from "@workspace/job/src/components/JobOverview/TemplatePreviewSideDrawer/TemplatePreviewSideDrawer";
import { format, set } from "date-fns";
import { useUserAuth } from "@workspace/login";

import { Link } from "react-router-dom";

const InterviewListing = () => {
  const { Permission, checkAllPermission } = useUserAuth();
  const dispatch = useDispatch();
  const formikRef = useRef();
  // Selectors
  const interviewData = useSelector(
    (state) => state?.interviewAnalyticsReducer?.interviewDetails?.data
  );
  const interviewCountData = useSelector(
    (state) => state?.interviewAnalyticsReducer?.interviewCounts?.data
  );
  const allInterviewCustomViews = useSelector(
    (state) => state?.interviewAnalyticsReducer?.interviewCustomViews
  );
  const interviewDefaultCustomView = useSelector(
    (state) => state?.interviewAnalyticsReducer?.interviewDefaultCustomView
  );

  const jobTimelineDataById = useSelector(
    (state) => state?.InterviewFormReducer?.jobTimelineData
  );

  /* States */
  const thisWeek = datePickerConstants.find(
    (dateRange) => Object.keys(dateRange)[0] === "This Week"
  )["This Week"];

  // Store from and to dates state
  const [fromDate, setFromDate] = useState(
    thisWeek.from ? format(thisWeek.from, "yyyy-MM-dd") : null
  );
  const [toDate, setToDate] = useState(
    thisWeek.to ? format(thisWeek.to, "yyyy-MM-dd") : null
  );

  // Status state
  const [status, setStatus] = useState("ALL");

  // Pagination and table state
  const [tableConfig, setTableConfig] = useState([]);
  const [loading, setLoading] = useState(false);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);

  //Custom View States
  const [interviewCustomView, setInterviewCustomView] = useState(null);
  const [interviewFilterBubble, setInterviewFilterBubble] = useState(null);

  // Modal states
  const [isCancel, setIsCancel] = useState(false);
  const [selectedInterviewForModal, setSelectedInterviewForModal] =
    useState(null);

  const [interviewDate, setInterviewDate] = useState(new Date());

  // To remove
  const [refreshKey, setRefreshKey] = useState(0);
  const triggerRefresh = () => {
    setRefreshKey((prevKey) => prevKey + 1);
  };

  // Offcanvas states
  const [offcanvasForm, setOffcanvasForm] = useState(false);
  const [activeOffCanvas, setActiveOffCanvas] = useState("");
  const [canvasWidth, setCanvasWidth] = useState("");
  const [isFullScreen, setIsFullScreen] = useState(false);

  //Which data do I need?
  const [interviewFormData, setInterviewFormData] = useState(null);
  const [jobTimeLineData, setJobTimeLineData] = useState([]);
  const [isViewTemplate, setIsViewTemplate] = useState(false);
  const [templatePreviewInfo, setTemplatePreviewInfo] = useState(null);
  const [templatePreviewAction, setTemplatePreviewAction] = useState(null);
  const [originalOrder, setOriginalOrder] = useState();
  const [activeStep, setActiveStep] = useState("");
  const [stepperState, setStepperState] = useState();
  const [readOnlyInterviewNo, setReadOnlyInterviewNo] = useState(0);
  const [candidateId, setCandidateId] = useState("");
  const [interviewId, setInterviewId] = useState("");
  const [readOnly, setReadOnly] = useState(false);
  const [associateEmail, setAssociateEmail] = useState(null);
  const [associateFormValues, setAssociateFormValues] = useState({});

  const [jobId, setJobId] = useState("");
  // Add function to handle modal
  const closeModal = () => {
    setIsModalOpen(false);
  };

  //Custom renders
  const customRenderList = [
    {
      names: ["updatedAt", "createdAt"],
      render: (data, opt) =>
        DateHelper.formatDateStandard2(
          DynamicTableHelper.getDynamicNestedResult(data, opt.value) || "-"
        ),
    },
    {
      names: ["job.jobSubmissionData.jobTitle"],
      render: (data) => {
        return (
          <>
            <Link
              className="text-decoration-underline"
              onClick={() => {
                const win = window.open(
                  `/jobs/${data?.job?.id}/snapshot`,
                  "_blank"
                );
                win.focus();
              }}
              style={{ color: "#0a56ae" }}
            >
              {data?.job?.jobSubmissionData?.jobTitle}
            </Link>
          </>
        );
      },
    },
    {
      names: ["status"],
      render: (data, opt) => {
        let badgeStyle = { backgroundColor: "dark" };
        switch (data.status) {
          case "SCHEDULED":
          case "RESCHEDULED":
            badgeStyle = { backgroundColor: "grey" };
            break;
          case "CANCELLED":
            badgeStyle = { backgroundColor: "red" };
            break;
          case "COMPLETED":
            badgeStyle = { backgroundColor: "green" };
            break;
          default:
            badgeStyle = { backgroundColor: "dark" };
        }
        return (
          <span className="badge" style={badgeStyle}>
            {data.status}
          </span>
        );
      },
    },
    {
      names: ["candidate.candidateSubmissionData.firstName"],
      render: (data, opt) => {
        return (
          <Link
            onClick={() => {
              const win = window.open(
                `/candidates/${data.candidate.id}/snapshot`,
                "_blank"
              );
              win.focus();
            }}
            style={{ color: "#0a56ae" }}
            className="text-custom-primary text-decoration-underline"
          >
            {data.candidate.candidateSubmissionData.firstName}
          </Link>
        );
      },
    },
  ];

  /* Handlers */
  // Date range change handler
  const handleDateRangeChange = (from, to) => {
    setFromDate(from);
    setToDate(to);
    setStatus("ALL");
    setLoading(true);
    const requestPayload = generateRequestPayload("ALL");
    dispatch(fetchInterviewListingDetails(requestPayload)).finally(() =>
      setLoading(false)
    );
  };

  // Status change handler
  const handleStatus = (status) => {
    const mappedStatus = statusMapping[status] || "ALL";
    setStatus(mappedStatus);
    const requestPayload = generateRequestPayload(mappedStatus); // Fetch based on status clicked
    dispatch(fetchInterviewListingDetails(requestPayload));
  };

  // Table States
  const {
    pageRequest,
    pageRequestSet,
    pageInfo,
    setPageInfoData,
    search,
    setSearch,
    customConfig,
    setCustomConfigData,
    setTableData,
    tableData,
    handleRowCheck,
    selectAllRows,
    activeRow,
    setActiveRow,
  } = useTableHook(
    {
      page: 0,
      pageSize: 50,
      sortBy: null,
      sortDirection: "asc",
      searchTerm: null,
      searchFields: [],
    },
    INTERVIEW_MANDATORY_OPTIONS,
    INTERVIEW_INITIAL_OPTIONS,
    customRenderList
  );

  //========================== User Setup ============================
  // This will vary with the table main page. Each table have it own config with additional columns

  const generateInterviewConfig = (customConfig) => {
    return [
      {
        header: "#",
        name: "indexing",
        sort: false,
        render: (data, index) => (
          <div className="d-flex column-gap-2">
            {pageInfo?.currentPage * pageInfo?.pageSize + (index + 1)}.
          </div>
        ),
      },
      {
        header: (
          <div className="form-check px-0">
            <Input
              className="form-check-input"
              type="checkbox"
              id="checkbox"
              checked={
                activeRow?.length > 0 && activeRow?.length === tableData?.length
              }
              onChange={(e) => selectAllRows(e?.target?.checked)}
            />
          </div>
        ),
        name: "checkbox",
        sort: false,
        render: (data) => {
          return (
            <div className="form-check px-0">
              <Input
                className="form-check-input"
                type="checkbox"
                name="chk_child"
                checked={activeRow?.includes(parseInt(data?.id))}
                onChange={(e) => handleRowCheck(data?.id, e.target.checked)}
              />
            </div>
          );
        },
      },
      ...customConfig,
      {
        header: "Action",
        name: "action",
        sort: false,
        sticky: "right",
        expand: true,
        center: true,
        render: (data) => (
          <ActionDropDown>
            <DropdownItem>
              <div
                className="d-flex  align-items-center gap-2"
                onClick={() => {
                  dispatch(
                    fetchJobCandidateId({
                      jobId: data?.job?.id,
                      candidateId: data?.candidate?.id,
                    })
                  );
                  setActiveStep(27);
                  setOriginalOrder(data.stageOrder);
                  openLogsModal(data);
                }}
              >
                <span>Interview Feedback</span>
              </div>
            </DropdownItem>
            <DropdownItem
              onClick={() => {
                dispatch(
                  fetchJobCandidateId({
                    jobId: data?.job?.id,
                    candidateId: data?.candidate?.id,
                  })
                );
                setReadOnly(false);
                setInterviewId(data.id);
                setActiveStep(15);
                setOriginalOrder(data.stageOrder);
                setActiveOffCanvas("reschedule_Interview");
                toggleOffcanvas();
              }}
            >
              <span type="button">
                <div className="d-flex align-items-center gap-2">
                  <span>Reschedule</span>
                </div>
              </span>
            </DropdownItem>
            <DropdownItem>
              <div
                className="d-flex align-items-center gap-2"
                onClick={() => {
                  dispatch(
                    fetchJobCandidateId({
                      jobId: data?.job?.id,
                      candidateId: data?.candidate?.id,
                    })
                  );
                  setActiveStep(16);
                  setOriginalOrder(data.stageOrder);
                  openCancelModal(data);
                }}
              >
                <span type="button">Cancel Interview</span>
              </div>
            </DropdownItem>
          </ActionDropDown>
        ),
      },
    ];
  };

  //==================================================================================================

  const generateRequestPayload = (statusOverride = null) => {
    const updatedStatus = statusOverride !== null ? statusOverride : status;

    // Start with the base filters (date range)
    const filters = [];

    if (interviewFilterBubble && interviewFilterBubble.length > 0) {
      interviewFilterBubble.forEach((filter, index) => {
        filters.push({
          condition: filter?.condition,
          field: filter?.field,
          label: filter?.label,
          operator: index === 0 ? "AND" : filter?.operator,
          value: filter?.value,
        });
      });
    }
    return {
      page: pageRequest?.page,
      pageSize: pageRequest?.pageSize,
      sortBy: pageRequest?.sortBy,
      sortDirection: pageRequest?.sortDirection,
      searchTerm: search,
      isDownload: false,
      filters: filters,
      externalFilters: [
        { label: "Date Range", value: fromDate, value2: toDate },
      ],
      searchFields: pageRequest?.searchFields,
      sortType: null,
      interviewStatus: updatedStatus,
    };
  };

  /* UseEffect */
  // UseEffect to fetch data when date, status, or pagination changes
  useEffect(() => {
    if (fromDate && toDate) {
      const payload = generateRequestPayload(); // Use current status and other filter
      setTableData([]); // Clear table before loading
      // Fetch interview listing with consolidated payload
      if (
        pageRequest.searchFields?.length > 0 &&
        interviewFilterBubble != null
      ) {
        dispatch(fetchInterviewListingDetails(payload));
      }

      // Fetch interview counts (if needed) separately
      dispatch(
        fetchInterviewCounts({
          startDate: fromDate,
          endDate: toDate,
        })
      );
    }
  }, [
    fromDate,
    toDate,
    pageRequest,
    // status,
    interviewFilterBubble,
    dispatch,
    refreshKey,
  ]);
  // Update the page info when interview Data changes
  useEffect(() => {
    if (interviewData) {
      setPageInfoData(interviewData);
      setTableData(interviewData?.interviews || []);
    }
  }, [interviewData]);
  // Update table config when custom config changes
  useEffect(() => {
    if (tableConfig) {
      const newConfig = generateInterviewConfig(customConfig);
      newConfig.forEach((item, index) => {
        const oldConfig = tableConfig?.find(
          (oldItem) => oldItem?.name === item?.name
        );
        // If cannot find then continue
        if (!oldConfig) return;
        if (oldConfig?.expand) {
          newConfig[index].expand = true;
        } else {
          newConfig[index].expand = false;
        }
      });
      setTableConfig(newConfig);
    } else {
      setTableConfig(generateInterviewConfig(customConfig));
    }
  }, [customConfig, pageInfo, activeRow, tableData]);
  //Set custom view state
  useEffect(() => {
    if (
      allInterviewCustomViews != null &&
      allInterviewCustomViews?.length > 0
    ) {
      const selectedCustomView = allInterviewCustomViews?.find(
        (customView) => customView?.selected
      );
      if (selectedCustomView) {
        setInterviewCustomView(selectedCustomView);
      } else {
        setInterviewCustomView(-1);
      }
    }
    if (
      allInterviewCustomViews != null &&
      allInterviewCustomViews?.length === 0
    ) {
      setInterviewCustomView(-1);
    }
  }, [allInterviewCustomViews]);

  const isEmpty = (obj) => Object.entries(obj).length === 0;
  // Interview Bubble Filter Setting
  useEffect(() => {
    if (interviewFilterBubble == null) {
      if (interviewCustomView != null) {
        if (interviewCustomView != -1) {
          setInterviewFilterBubble(interviewCustomView?.filters);
        } else if (interviewDefaultCustomView) {
          if (isEmpty(interviewDefaultCustomView)) {
            setInterviewFilterBubble([]);
          } else {
            setInterviewFilterBubble(interviewDefaultCustomView?.filters);
          }
        }
      }
    }
  }, [interviewCustomView, interviewDefaultCustomView]);

  // Full screen toggle -- Optimize this...
  useEffect(() => {
    if (!isFullScreen && activeOffCanvas !== "reschedule_Interview") {
      switch (activeOffCanvas) {
        case "Job_listing_canvas":
          setCanvasWidth("80vw");
          break;
        case "reschedule_Interview":
          setCanvasWidth("100vw");
          break;
        default:
          setCanvasWidth("80vw");
      }
    }
  }, [activeOffCanvas, isFullScreen]);

  useEffect(() => {
    // if (activeStep === 13) {
    let formName = "Schedule First Interview";
    if (activeStep === 13 && !readOnly) {
      if (originalOrder === 10) {
        formName = "Schedule Second Interview";
        setReadOnlyInterviewNo(1);
      } else if (originalOrder === 11) {
        formName = "Schedule Third Interview";
        setReadOnlyInterviewNo(2);
      } else if (originalOrder === 12) {
        formName = "Schedule Third Interview";
        setReadOnlyInterviewNo(3);
      }
    } else if (activeStep === 15 && !readOnly) {
      if (originalOrder === 10) {
        formName = "Reschedule First Interview";
        setReadOnlyInterviewNo(1);
      } else if (originalOrder === 11) {
        formName = "Reschedule Second Interview";
        setReadOnlyInterviewNo(2);
      } else if (originalOrder === 12) {
        formName = "Reschedule Third Interview";
        setReadOnlyInterviewNo(3);
      }
    } else if (activeStep === 15 && readOnly) {
      if (originalOrder === 10) {
        formName = "First Interview";
        setReadOnlyInterviewNo(1);
      } else if (originalOrder === 11) {
        formName = "Second Interview";
        setReadOnlyInterviewNo(2);
      } else if (originalOrder === 12) {
        formName = "Third Interview";
        setReadOnlyInterviewNo(3);
      }
    }

    setStepperState(formName);
  }, [originalOrder, activeStep, readOnly]);

  useEffect(() => {
    if (jobTimelineDataById && jobTimelineDataById?.timeline) {
      setJobTimeLineData(jobTimelineDataById);
    }
  }, [jobTimelineDataById]);

  //========================== Cancel Interview ============================
  // Cancel Interview Handler
  const handleCancelInterview = (interview) => {
    const payload = {
      status: "CANCELLED",
      cancellationReason: interview.cancellationReason,
      timeTaken: interview.timeTaken,
    };

    dispatch(
      cancelInterview({
        interviewId: interview.id,
        interview: payload,
        date: interviewDate,
        setRefreshKey,
      })
    );
    setIsModalOpen(false);
  };

  const openCancelModal = (interview) => {
    setSelectedInterviewForModal(interview);
    setIsCancel(true);
    setIsModalOpen(true);
  };

  //==========================  Interview Logs ============================
  // Interview Logs Handler
  const handleInterviewLogs = (interview) => {
    // To be removed... Follow the same pattern as cancel interview
    const payload = {
      status: "INTERVIEWLOGS",
      logsComment: interview.logsComment,
    };

    dispatch(addInterviewLogs(interview.id, payload, setRefreshKey));
    setIsModalOpen(false);
    triggerRefresh();
  };

  const openLogsModal = (interview) => {
    setSelectedInterviewForModal(interview);
    setIsCancel(false);
    setIsModalOpen(true);
  };

  //==========================  Off Canvas Setup ============================
  const closeOffCanvas = () => {
    setOffcanvasForm(false);
  };

  const toggleOffcanvas = () => {
    setOffcanvasForm(!offcanvasForm);
  };

  // Full-screen toggle for "reschedule_Interview" only
  const toggleFullScreen = () => {
    setCanvasWidth(canvasWidth === "100vw" ? "80vw" : "100vw");
  };

  const handleJobListCanvas = () => {
    //Change to candidate canvas
    setOffcanvasForm(true);
    setActiveOffCanvas("Job_listing_canvas");
  };

  // Function to handle row selection and populate form data
  const handleSelectInterview = (interview) => {
    setInterviewFormData(interview);
    setOffcanvasForm(true);
  };

  // for change -> Sabrina's new UI
  const generateCanvasHeaderButton = () => {
    let showCancel = true;
    let showSubmit = true;

    switch (activeOffCanvas) {
      case "view_Interview":
        return (
          <Button
            className="btn btn-white bg-gradient border-2 border-light-grey fw-semibold"
            style={{
              borderRadius: "8px",
            }}
            onClick={() => {
              fetchJobTimelineFormSubmissionReset();
              setOffcanvasForm(false);
              setIsViewTemplate(false);
            }}
          >
            Cancel
          </Button>
        );
      case "reschedule_Interview":
        // If reschedule return cancel and update
        return (
          <Row>
            <Col>
              <div className="d-flex justify-content-end gap-2">
                <Button
                  style={{
                    backgroundColor: "#FFFFFF",
                    border: "1px solid #E7EAEE",
                    color: "#000000",
                    fontWeight: "500",
                    borderRadius: "8px",
                  }}
                  type="button"
                  className="d-flex align-items-center justify-content-center"
                  onClick={toggleFullScreen}
                >
                  <span className="mdi mdi-dock-right"></span>
                </Button>
                <Button
                  className="btn btn-white bg-gradient border-2 border-light-grey fw-semibold"
                  style={{
                    borderRadius: "8px",
                  }}
                  onClick={() => {
                    fetchJobTimelineFormSubmissionReset();
                    setOffcanvasForm(false);
                    // setActiveOffCanvas("Job_listing_canvas");
                  }}
                >
                  Cancel
                </Button>
                <Button
                  className="btn btn-success"
                  onClick={() => {
                    formikRef.current.submitForm();
                  }}
                  style={{
                    borderRadius: "8px",
                  }}
                >
                  <span>Send</span>
                </Button>
              </div>
            </Col>
          </Row>
        );
      case "new_Interview":
        // If new interview return cancel and update
        return (
          <Row>
            <Col>
              <div className="d-flex justify-content-end gap-2">
                <Button
                  style={{
                    backgroundColor: "#FFFFFF",
                    border: "1px solid #E7EAEE",
                    color: "#000000",
                    fontWeight: "500",
                    borderRadius: "8px",
                  }}
                  type="button"
                  className="d-flex align-items-center justify-content-center"
                  onClick={toggleFullScreen}
                >
                  <span className="mdi mdi-dock-right"></span>
                </Button>
                <Button
                  className="btn btn-white bg-gradient border-2 border-light-grey fw-semibold"
                  style={{
                    borderRadius: "8px",
                  }}
                  onClick={() => {
                    // setOffcanvasForm(false);
                    fetchJobTimelineFormSubmissionReset();
                    setActiveOffCanvas("Job_listing_canvas");
                  }}
                >
                  Cancel
                </Button>
                <Button
                  className="btn btn-success"
                  onClick={() => {
                    formikRef.current.submitForm();
                  }}
                  style={{
                    borderRadius: "8px",
                  }}
                >
                  <span>Send</span>
                </Button>
              </div>
            </Col>
          </Row>
        );
      default:
        return null;
    }
  };

  const renderCanvasContent = () => {
    switch (activeOffCanvas) {
      case "view_Interview":
        return (
          <>
            <OffCanvasHeaderComponent
              generateCanvasHeaderButton={generateCanvasHeaderButton}
              stepperState={stepperState}
              jobTimeLineData={jobTimeLineData}
              readOnly={readOnly}
            />

            <NewInterviewCanvas
              offcanvasForm={offcanvasForm}
              setOffcanvasForm={setOffcanvasForm}
              setIsViewTemplate={setIsViewTemplate}
              setTemplatePreviewInfo={setTemplatePreviewInfo}
              setTemplatePreviewAction={setTemplatePreviewAction}
              jobTimeLineData={jobTimeLineData}
              originalOrder={originalOrder}
              activeStep={activeStep}
              ref={formikRef}
              readOnly={readOnly}
              readOnlyInterviewNo={readOnlyInterviewNo}
              offcanvasWidth={canvasWidth}
              setOffcanvasWidth={setCanvasWidth}
              setRefreshKey={setRefreshKey}
              interviewId={interviewId}
            />
          </>
        );
      case "reschedule_Interview":
        return (
          <>
            <OffCanvasHeaderComponent
              generateCanvasHeaderButton={generateCanvasHeaderButton}
              stepperState={stepperState}
              jobTimeLineData={jobTimeLineData}
              readOnly={readOnly}
            />
            <NewInterviewCanvas
              closeOffCanvas={closeOffCanvas}
              setOffcanvasForm={setOffcanvasForm}
              setIsViewTemplate={setIsViewTemplate}
              setTemplatePreviewInfo={setTemplatePreviewInfo}
              setTemplatePreviewAction={setTemplatePreviewAction}
              jobTimeLineData={jobTimeLineData}
              originalOrder={originalOrder}
              activeStep={activeStep}
              ref={formikRef}
              readOnly={readOnly}
              readOnlyInterviewNo={readOnlyInterviewNo}
              offcanvasWidth={canvasWidth}
              setOffcanvasWidth={setCanvasWidth}
              setRefreshKey={setRefreshKey}
              interviewId={interviewId}
            />
          </>
        );
      case "Job_listing_canvas":
        return (
          <InterviewCandidateCanvas
            setCandidateId={setCandidateId}
            setActiveStep={setActiveStep}
            setJobTimeLineData={setJobTimeLineData}
            toggleOffcanvas={toggleOffcanvas}
            setActiveOffCanvas={setActiveOffCanvas}
            setOriginalOrder={setOriginalOrder}
            getMaxOrder={getMaxOrder}
            setReadOnly={setReadOnly}
          />
        );
      case "new_Interview":
        return (
          <>
            <OffCanvasHeaderComponent
              generateCanvasHeaderButton={generateCanvasHeaderButton}
              stepperState={stepperState}
              jobTimeLineData={jobTimeLineData}
              readOnly={readOnly}
            />
            <NewInterviewCanvas
              closeOffCanvas={closeOffCanvas}
              setOffcanvasForm={setOffcanvasForm}
              setIsViewTemplate={setIsViewTemplate}
              setTemplatePreviewInfo={setTemplatePreviewInfo}
              setTemplatePreviewAction={setTemplatePreviewAction}
              jobTimeLineData={jobTimeLineData}
              originalOrder={originalOrder}
              activeStep={activeStep}
              ref={formikRef}
              readOnly={readOnly}
              readOnlyInterviewNo={readOnlyInterviewNo}
              offcanvasWidth={canvasWidth}
              setOffcanvasWidth={setCanvasWidth}
              setRefreshKey={setRefreshKey}
            />
          </>
        );
      default:
        return null;
    }
  };

  const getMaxOrder = (data) => {
    const values = Object?.values(data?.timeline)?.flat();
    let maxOrder = 1;
    if (values) {
      let orders = values?.map((item) => item?.order);
      if (orders) {
        orders = orders.sort((a, b) => b - a);
        maxOrder = orders?.[0] ?? 1;
      }
    }
    return maxOrder;
  };
  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Row>
            <Col lg={12}>
              <Row className="mb-2">
                <Col>
                  <h4 className="d-flex gap-1 pt-2">
                    <span>Interview</span>
                  </h4>
                </Col>
              </Row>
              <div className="h-100">
                <Row>
                  <AnalyticsCard
                    onDateRangeChange={handleDateRangeChange}
                    status={status}
                    handleStatus={handleStatus}
                  />
                  <InterviewCard
                    toggleOffcanvas={toggleOffcanvas}
                    handleSelectInterview={handleSelectInterview}
                    setReadOnly={setReadOnly}
                    setActiveOffCanvas={setActiveOffCanvas}
                    openCancelModal={openCancelModal}
                    openLogsModal={openLogsModal}
                    interviewDate={interviewDate}
                    setInterviewDate={setInterviewDate}
                    refreshKey={refreshKey}
                    getMaxOrder={getMaxOrder}
                    setActiveStep={setActiveStep}
                    setJobTimeLineData={setJobTimeLineData}
                    setOriginalOrder={setOriginalOrder}
                    setInterviewId={setInterviewId}
                    direction={"end"}
                  />
                </Row>
              </div>
            </Col>
            <Col lg={12}>
              {loading ? (
                <div>Loading...</div>
              ) : (
                <>
                  <ModalFormWrapper
                    originalOrder={originalOrder}
                    activeStep={activeStep}
                    header={stepperState}
                    isFormModalOpen={isModalOpen}
                    isCancel={isCancel}
                    closeModal={closeModal}
                    jobTimeLineData={jobTimeLineData}
                    readOnlyInterviewNo={readOnlyInterviewNo}
                    handleInterviewLogs={handleInterviewLogs}
                    handleCancelInterview={handleCancelInterview}
                    interview={selectedInterviewForModal}
                    setRefreshKey={setRefreshKey}
                    interviewDate={interviewDate}
                  />
                  <DynamicTableWrapper
                    data={interviewData?.interviews}
                    config={tableConfig}
                    pageInfo={pageInfo}
                    pageRequest={pageRequest}
                    pageRequestSet={pageRequestSet}
                    search={search}
                    setSearch={setSearch}
                    setCustomConfigData={setCustomConfigData}
                    header="Interviews"
                    activeRow={activeRow}
                    setActiveRow={setActiveRow}
                    setTableConfig={setTableConfig}
                    fromDate={fromDate}
                    toDate={toDate}
                    interviewCustomView={interviewCustomView}
                    setInterviewCustomView={setInterviewCustomView}
                    interviewFilterBubble={interviewFilterBubble}
                    setInterviewFilterBubble={setInterviewFilterBubble}
                    toggleOffcanvas={toggleOffcanvas}
                    handleJobListCanvas={handleJobListCanvas}
                  />
                </>
              )}
            </Col>
          </Row>
          {/* <OffCanvasWrapper
            isFilterOpen={offcanvasForm}
            closeFilter={toggleOffcanvas}
            isScrollable={true}
            width={canvasWidth}
          >
            {renderCanvasContent()}
            <TemplatePreviewSideDrawer
              showSideDrawer={isViewTemplate}
              setShowSideDrawer={setIsViewTemplate}
              templatePreviewInfo={templatePreviewInfo}
              templatePreviewAction={templatePreviewAction}
              jobTimeLineData={jobTimeLineData}
              fetchSingleTemplate={{
                category: "Email Templates",
                template: "Associate",
              }}
              formValues={[associateFormValues]}
              activeStep={activeStep}
              associateEmail={associateEmail}
              setAssociateEmail={setAssociateEmail}
              readOnly={readOnly}
            />
          </OffCanvasWrapper> */}
          <Offcanvas
            isOpen={offcanvasForm}
            toggle={() => toggleOffcanvas()}
            direction="end"
            // Previously 55vw
            style={{
              width: canvasWidth,
            }}
          >
            <OffcanvasBody className="p-0">
              {renderCanvasContent()}
            </OffcanvasBody>
            <TemplatePreviewSideDrawer
              showSideDrawer={isViewTemplate}
              setShowSideDrawer={setIsViewTemplate}
              templatePreviewInfo={templatePreviewInfo}
              templatePreviewAction={templatePreviewAction}
              jobTimeLineData={jobTimeLineData}
              fetchSingleTemplate={{
                category: "Email Templates",
                template: "Associate",
              }}
              formValues={[associateFormValues]}
              activeStep={activeStep}
              associateEmail={associateEmail}
              setAssociateEmail={setAssociateEmail}
              readOnly={readOnly}
            />
          </Offcanvas>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default InterviewListing;
